import React from "react";
import { NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import { Home, Receipt, ArrowUpCircle, Gauge, UserCircle2, LogOut, Wifi, KeyRound } from "lucide-react";
import { usePortalAuth } from "./PortalAuthContext";
import { useBranding } from "../hooks/useBranding";

const NAV = [
  { to: "/portal", label: "Beranda", icon: Home, end: true, testid: "portal-nav-home" },
  { to: "/portal/tagihan", label: "Tagihan", icon: Receipt, testid: "portal-nav-tagihan" },
  { to: "/portal/upgrade", label: "Upgrade", icon: ArrowUpCircle, testid: "portal-nav-upgrade" },
  { to: "/portal/speedtest", label: "Speedtest", icon: Gauge, testid: "portal-nav-speedtest" },
  { to: "/portal/akun", label: "Akun", icon: UserCircle2, testid: "portal-nav-akun" },
];

export const PortalProtected = ({ children }) => {
  const { customer, loading } = usePortalAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#070c18] flex items-center justify-center text-emerald-400 font-mono text-sm">
        <div className="w-8 h-8 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!customer) return <Navigate to="/portal/login" replace />;
  return children;
};

export const PortalLayout = () => {
  const { customer, logout } = usePortalAuth();
  const branding = useBranding();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#070c18] text-slate-200" data-testid="portal-layout">
      <header className="sticky top-0 z-30 bg-[#0d1527]/90 backdrop-blur border-b border-slate-800">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-emerald-500 to-cyan-600 flex items-center justify-center overflow-hidden shrink-0">
              {branding.app_logo ? <img src={branding.app_logo} alt="Logo" className="w-full h-full object-contain" /> : <Wifi className="w-4 h-4 text-white" />}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-white leading-tight truncate">{branding.app_name || "NUSA NET FIBER"}</p>
              <p className="text-[10px] text-slate-400 uppercase tracking-wider">Portal Pelanggan</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-white truncate max-w-[160px]" data-testid="portal-customer-name">{customer?.nama}</p>
              <p className="text-[10px] text-slate-400 font-mono">{customer?.no_layanan}</p>
            </div>
            <button onClick={() => { logout(); navigate("/portal/login"); }} className="p-2 rounded-lg text-slate-400 hover:text-rose-400 hover:bg-rose-500/10" title="Keluar" data-testid="portal-logout-btn">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 pt-4 pb-24 space-y-4">
        {customer?.password_default && (
          <NavLink to="/portal/akun" className="flex items-center gap-2 p-3 rounded-xl bg-amber-500/10 border border-amber-500/40 text-amber-200 text-xs hover:bg-amber-500/15" data-testid="portal-default-password-banner">
            <KeyRound className="w-4 h-4 shrink-0" />
            <span>Anda masih memakai kata sandi default <b>123456</b>. Ketuk untuk menggantinya sekarang.</span>
          </NavLink>
        )}
        <Outlet />
      </main>

      <nav className="fixed bottom-0 inset-x-0 z-30 bg-[#0d1527]/95 backdrop-blur border-t border-slate-800" data-testid="portal-bottom-nav">
        <div className="max-w-3xl mx-auto grid grid-cols-5">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold transition-colors ${isActive ? "text-emerald-400" : "text-slate-500 hover:text-slate-300"}`}>
              <n.icon className="w-5 h-5" />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};
